import createNotification, {
  createErrorNotification,
} from "../../../app/notifications/createNotification";

import { ICognitoUserWithOrgData } from "@tedivo/tvd-api-models";
import { ITableOnClickOptions } from "@tedivo/tedivo-smart-table";
import Services from "../../../app/services";
import {
  TCognitoRemediateAction,
} from "../../../app/services/admin/organizations";
import { getTranslation } from "../../../app/i18/i18tn";

/** Returns the context-sensitive remediation options for a user row */
export function getRemediateOptions(
  dta: ICognitoUserWithOrgData,
  isTedivoAdmin: boolean
): ITableOnClickOptions[] {
  const options: (ITableOnClickOptions | undefined)[] = [];

  // Stall states that can be fixed by setting a new permanent password
  if (
    dta.userStatusType === "FORCE_CHANGE_PASSWORD" ||
    dta.userStatusType === "RESET_REQUIRED" ||
    dta.userStatusType === "COMPROMISED"
  ) {
    options.push({
      label: getTranslation("general:users.remediateActions.force-confirm"),
      value: "force-confirm",
      icon: "shield-check",
    });
  }

  // User email never verified
  if (dta.userStatusType === "UNCONFIRMED") {
    options.push({
      label: getTranslation("general:users.remediateActions.confirm"),
      value: "confirm",
      icon: "envelope-check",
    });
  }

  // Archived: cannot be restored, but can be re-invited
  // if (dta.userStatusType === "ARCHIVED") {
  //   options.push({
  //     label: getTranslation("general:users.remediateActions.reinvite"),
  //     value: "reinvite",
  //     icon: "envelope-plus",
  //   });
  // }

  if (isTedivoAdmin) {
    // Cognito-level disabled
    if (dta.cognitoEnabled === false) {
      options.push({
        label: getTranslation("general:users.remediateActions.enable"),
        value: "enable",
        icon: "person-check",
      });
    }

    // Allow admins to Cognito-disable active confirmed users
    if (
      dta.cognitoEnabled !== false &&
      (dta.userStatusType === "CONFIRMED" ||
        dta.userStatusType === "EXTERNAL_PROVIDER")
    ) {
      options.push({
        label: getTranslation("general:users.remediateActions.disable"),
        value: "disable",
        icon: "person-dash",
        variant: "danger",
      });
    }
  }

  return options.filter(Boolean) as ITableOnClickOptions[];
}

/** Returns the CSS class name to apply to the status cell based on user's state */
export function getUserStatusClassName(dta: ICognitoUserWithOrgData | undefined): string {
  if (!dta) return "";

  switch (dta.userStatusType) {
    case "FORCE_CHANGE_PASSWORD":
    case "RESET_REQUIRED":
    case "UNCONFIRMED":
      return "state-warning";
    case "COMPROMISED":
    case "ARCHIVED":
      return "state-danger";
    case "EXTERNAL_PROVIDER":
      return "state-highlighted";
    default:
      return dta.cognitoEnabled === false ? "state-warning" : "";
  }
}

/**
 * Handles a remediation action click from the table.
 * Returns a function that executes the given action and optionally refreshes the view.
 */
export function handleRemediateAction({
  orgId,
  dta,
  action,
  onReInvite,
  onRefresh,
}: {
  orgId: string;
  dta: ICognitoUserWithOrgData;
  action: string;
  onReInvite?: (dta: ICognitoUserWithOrgData) => void;
  onRefresh: () => void;
}) {
  if (action === "reinvite") {
    if (onReInvite) onReInvite(dta);
    return;
  }

  const validActions: TCognitoRemediateAction[] = [
    "force-confirm",
    "confirm",
    "enable",
    "disable",
  ];

  if (!validActions.includes(action as TCognitoRemediateAction)) return;

  Services.organizations
    .adminRemediateOrganizationUser(
      orgId,
      dta.sub,
      action as TCognitoRemediateAction,
    )
    .then((resp) => {
      if (resp.statusCode === 200) {
        createNotification(
          getTranslation("general:users.remediateActions.successMessage", {
            action,
          }),
          "success",
          "check-circle",
        );
        onRefresh();
      } else {
        createErrorNotification(
          getTranslation("general:users.remediateActions.errorMessage", {
            action,
          }),
        );
      }
    })
    .catch(() => {
      createErrorNotification(
        getTranslation("general:users.remediateActions.errorMessage", {
          action,
        }),
      );
    });
}
